import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../context/AuthContext';
import Header from './shared/Header';
import Footer from './Footer';
import DisclaimerFooter from './DisclaimerFooter';
import SEO from './SEO';

export default function PowerListPage() {
  const [powerListData, setPowerListData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchPowerList = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/ea_stock_rankings?type=ea_power_list`, {
          headers
        });
        setPowerListData(response.data);
      } catch (error) {
        console.error('Error fetching power list:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPowerList();
  }, [user]); // Re-fetch when user authentication state changes

  const handleSecurityClick = (security) => {
    if (user) {
      // User is authenticated, navigate to ticker page
      navigate(`/ticker/${security.security_id}`, { 
        state: { 
          security,
          returnTo: {
            path: '/power-list'
          }
        }
      });
    } else {
      // User is not authenticated, redirect to login
      navigate('/login', { 
        state: { message: 'Log in to view detailed security information' }
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  // Create preview list: For authenticated users show all data, for non-authenticated users mask ranks 1-10
  const createPreviewList = () => {
    if (!powerListData?.rankings) return [];
    
    const previewList = [];
    
    if (user) {
      // Authenticated user - show all data (first 15 for display purposes)
      for (let i = 0; i < Math.min(15, powerListData.rankings.length); i++) {
        const stock = powerListData.rankings[i];
        if (stock) {
          previewList.push({
            ...stock,
            is_masked: false
          });
        }
      }
    } else {
      // Non-authenticated user - mask ranks 1-10, show ranks 11-15
      // Add ranks 1-10 as masked
      for (let i = 0; i < 10; i++) {
        const stock = powerListData.rankings[i];
        if (stock) {
          previewList.push({
            ...stock,
            is_masked: true
          });
        }
      }
      
      // Add ranks 11-15 as actual data
      for (let i = 10; i < 15; i++) {
        const stock = powerListData.rankings[i];
        if (stock) {
          previewList.push({
            ...stock,
            is_masked: false
          });
        }
      }
    }
    
    return previewList;
  };

  const previewList = createPreviewList();

  return (
    <>
      <SEO 
        title="Power List"
        description="Discover SharpeMetrix Power List™ - our proprietary ranking methodology combining fundamental analysis, market sentiment, and alternative data to identify the most promising investment opportunities."
        keywords="Power List, stock rankings, investment opportunities, fundamental analysis, market sentiment, alternative data, proprietary methodology"
        url="/power-list"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-25"
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-2xl pt-24 text-center sm:pt-40">
              <h2 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
                SharpeMetrix Power List™
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-300">
                Our proprietary ranking methodology combines fundamental analysis, market sentiment, 
                and alternative data to identify the most promising investment opportunities. 
                Updated monthly, this list represents our highest-conviction stock selections 
                based on our systematic approach to market analysis.
              </p>
            </div>
          </div>

          {/* Power List Preview Table */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-16">
            <div className="bg-gray-800/50 rounded-lg shadow p-4">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h3 className="text-base font-semibold leading-6 text-white">
                    {user ? 'Current Power List (Top 15)' : 'Current Power List Preview'}
                  </h3>
                  {powerListData?.as_of_date && (
                    <p className="mt-2 text-sm text-gray-400">
                      Data as of: {powerListData.as_of_date}
                    </p>
                  )}
                </div>
                {user && (
                  <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <button
                      onClick={() => navigate('/full-power-list')}
                      className="rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                    >
                      View Full List
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-8 flow-root">
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead>
                        <tr>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">Rank</th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">Ticker</th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">Company Name</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {previewList.map((stock) => (
                          <tr 
                            key={stock.security_id}
                            onClick={() => handleSecurityClick(stock)}
                            className={`${!stock.is_masked ? 'hover:bg-gray-700/50 cursor-pointer' : 'bg-gray-800/30'} ${user ? 'hover:bg-gray-700/50 cursor-pointer' : ''}`}
                          >
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                              {stock.ranking}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                              {stock.is_masked ? (
                                <div className="flex items-center">
                                  <span className="inline-flex items-center rounded-full bg-gray-700/50 px-2 py-1 text-xs text-gray-400">
                                    <LockClosedIcon className="h-3 w-3 mr-1" />
                                    Premium
                                  </span>
                                </div>
                              ) : (
                                stock.ticker
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                              {stock.is_masked ? (
                                <span className="inline-flex items-center rounded-full bg-gray-700/50 px-2 py-1 text-xs text-gray-400">
                                  <LockClosedIcon className="h-3 w-3 mr-1" />
                                  Subscribe to view
                                </span>
                              ) : (
                                stock.name
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Login CTA Section - only show for non-authenticated users */}
              {!user && (
                <div className="mt-8 bg-indigo-500/10 rounded-lg p-8 text-center">
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Want to see the complete Power List?
                  </h4>
                  <p className="text-gray-300 mb-6">
                    Log in to view all 25,000+ additional stocks and get access to detailed analytics.
                  </p>
                  <div className="flex justify-center gap-4">
                    <a
                      href="/login"
                      className="rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                    >
                      Log In
                    </a>
                    <a
                      href="/register"
                      className="rounded-md bg-white/10 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      Start Free Trial
                    </a>
                  </div>
                </div>
              )}

              {/* Authenticated user info section */}
              {user && (
                <div className="mt-8 bg-green-500/10 rounded-lg p-8 text-center">
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Welcome back! 
                  </h4>
                  <p className="text-gray-300 mb-6">
                    Click on any stock above to view detailed analytics and insights. 
                    Use the "View Full List" button to see all rankings.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-24 sm:mt-32">
            <div className="relative isolate">
              <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto flex max-w-2xl flex-col gap-16 bg-white/5 px-8 py-16 ring-1 ring-white/10 sm:rounded-3xl sm:px-12 sm:py-20 lg:mx-0 lg:max-w-none lg:flex-row lg:items-center xl:gap-x-20 xl:px-20">
                  <div className="w-full flex-auto text-center lg:text-left">
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                      {!user ? 'Want access to our full Power List?' : 'Want to learn more?'}
                    </h2>
                    <p className="mt-6 text-lg leading-8 text-gray-300">
                      {!user ? 
                        'Contact us to get approved for full access to our complete rankings of 25,000+ stocks and detailed analytics.' :
                        'Contact us to discover how our systematic approach and alternative data insights can enhance your investment strategy.'
                      }
                    </p>
                    <div className="mt-8 flex justify-center lg:justify-start">
                      <button
                        onClick={() => navigate('/contact')}
                        className="inline-flex items-center rounded-lg bg-primary-100 px-6 py-3 text-base font-semibold text-white shadow-lg hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100 transition-all duration-300 hover:shadow-xl hover:shadow-primary-100/25"
                      >
                        {!user ? 'Get Full Access' : 'Contact Us'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
      <DisclaimerFooter small />
    </>
  );
} 